"use client";

import { useRouter } from "nextjs-toploader/app";
import { ReactNode, createContext, useCallback, useContext, useLayoutEffect, useMemo, useState, useTransition } from "react";
import { handleDisableDraft } from "@/lib/storyblok/storyblok";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { isDraftMode } from "@/lib/storyblok/is-draft-mode";
interface DraftModeProviderProps {
  draftMode?: boolean;
  children?: ReactNode;
}
interface DraftModeContextProps {
  draftMode: boolean;
}
const DraftModeContext = createContext<DraftModeContextProps | undefined>(undefined);
export const DraftModeProvider = ({
  children,
  draftMode = false
}: DraftModeProviderProps) => {
  const [isDraftModeEnabled, setIsDraftModeEnabled] = useState(false);
  const checkDraftMode = useCallback(async () => {
    const result = isDraftMode(draftMode);
    setIsDraftModeEnabled(result);
  }, [draftMode]);
  useLayoutEffect(() => {
    checkDraftMode();
  }, [checkDraftMode]);
  const data = useMemo(() => ({
    draftMode: isDraftModeEnabled
  }), [isDraftModeEnabled]);
  const {
    refresh
  } = useRouter();
  const [isPending, startTransition] = useTransition();
  const handleOnClick = useCallback(async () => {
    await handleDisableDraft();
    refresh();
  }, [refresh]);
  return <>
      <DraftModeContext.Provider value={data} data-sentry-element="unknown" data-sentry-source-file="DraftModeProvider.tsx">
        {children}
      </DraftModeContext.Provider>
      {isDraftModeEnabled && <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button className="group fixed bottom-4 left-4 z-50" variant="destructive" onClick={() => startTransition(handleOnClick)}>
                {isPending ? "Loading..." : "Draft mode!"}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Clicking on the button will disable the draft mode</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>}
    </>;
};
export const useDraftModeContext = () => {
  const context = useContext(DraftModeContext);
  if (context === undefined) {
    throw new Error("Hook useDraftModeContext must be used within a DraftModeProvider component");
  }
  return context;
};